/**
 * Smart ad slot. Behavior:
 *   - Master OFF        → renders nothing (zero footprint, SEO-safe).
 *   - Master ON, slot configured → renders the real AdSense/Adsterra ad.
 *   - Master ON, slot NOT yet configured → renders a clearly-labeled
 *     "Ads / إعلان (موضع تجريبي)" placeholder so the admin can visually
 *     confirm where ads will appear on every page.
 *
 * Designed to be SEO-friendly: rendered as <aside aria-label="إعلان">,
 * lazy-mounted via IntersectionObserver for real ads, and wrapped with a
 * small "إعلان" label so it stays compliant with AdSense placement policies.
 */
import { useEffect, useRef, useState } from "react";
import { useSiteAds, resolveSlot, type AdSlotKey } from "@/lib/site-ads";
import { useTheme } from "@/lib/theme-context";

interface Props {
  slot: AdSlotKey;
  /** Optional className applied to the outer wrapper. */
  className?: string;
  /** Hint for layout: "horizontal" gives banner-ish min-height, "square" gives square min-height. */
  format?: "horizontal" | "square" | "auto";
}

export function AdSlot({ slot, className = "", format = "auto" }: Props) {
  const { language } = useTheme();
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);
  const [cfg] = useSiteAds();
  const resolved = resolveSlot(cfg, slot);

  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [rendered, setRendered] = useState(false);

  // Lazy mount — only render the actual ad when in/near viewport.
  useEffect(() => {
    if (!resolved || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [resolved]);

  // For AdSense, kick off the (adsbygoogle = window.adsbygoogle || []).push({}).
  useEffect(() => {
    if (!visible || !resolved || rendered) return;
    if (resolved.network !== "adsense") return;
    try {
      // @ts-expect-error injected by AdSense loader script
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      setRendered(true);
    } catch (err) {
      console.warn("[AdSlot] AdSense push failed", err);
    }
  }, [visible, resolved, rendered]);

  // Master OFF — render nothing at all.
  if (!cfg.masterEnabled) return null;

  const minH =
    format === "horizontal" ? "min-h-[90px]" : format === "square" ? "min-h-[250px]" : "min-h-[120px]";

  // Master ON but this slot isn't fully wired (slot disabled, no publisher
  // ID, no Adsterra code, etc.) — render a visible placeholder so the admin
  // sees exactly where ads will land once configured.
  if (!resolved) {
    const slotCfg = cfg.slots[slot];
    return (
      <aside
        aria-label={t("موضع إعلان", "Ad slot", "Emplacement publicitaire")}
        role="complementary"
        className={`my-4 w-full rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 p-3 text-center ${className}`}
      >
        <div className="mb-1 text-[10px] uppercase tracking-wider text-primary/80">
          {t("إعلان · موضع إعلان", "Ads · Ad slot", "Pub · Emplacement publicitaire")}
        </div>
        <div className={`w-full ${minH} grid place-items-center overflow-hidden`}>
          <div className="space-y-1">
            <div className="text-2xl font-bold text-primary/70">Ads</div>
            <div className="text-[11px] text-muted-foreground">
              {slotCfg?.label ?? slot} — {t("لم يتم تفعيل / إعداد هذا الموضع بعد", "this slot is not enabled / configured yet", "cet emplacement n’est pas encore activé / configuré")}
            </div>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside
      ref={ref}
      aria-label={t("إعلان", "Advertisement", "Publicité")}
      role="complementary"
      className={`my-4 w-full rounded-xl border border-dashed border-border bg-card/40 p-2 text-center ${className}`}
    >
      <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{t("إعلان", "Advertisement", "Publicité")}</div>
      <div className={`w-full ${minH} grid place-items-center overflow-hidden`}>
        {visible ? (
          resolved.network === "adsense" ? (
            <ins
              className="adsbygoogle block w-full"
              style={{ display: "block" }}
              data-ad-client={cfg.adsense.publisherId}
              data-ad-slot={resolved.adsenseSlotId || ""}
              data-ad-format="auto"
              data-full-width-responsive="true"
            />
          ) : resolved.adsterraCode ? (
            <div
              className="w-full"
              // Adsterra provides raw <script> snippets — they need to be
              // mounted as innerHTML so the embedded <script> executes.
              dangerouslySetInnerHTML={{ __html: resolved.adsterraCode }}
            />
          ) : (
            <span className="text-xs text-muted-foreground">{t("لم يتم إعداد كود الإعلان", "Ad code is not configured", "Le code publicitaire n’est pas configuré")}</span>
          )
        ) : (
          <span className="text-xs text-muted-foreground/60">…</span>
        )}
      </div>
    </aside>
  );
}
